import type {
  ChatSession,
  DirectoryMap,
  GroupMap,
  SessionKind,
  SessionMap,
} from '@domain/chat/types';
import { initials, userStatusLabel } from '@shared/utils/ui';

export type SessionPresentation = Pick<ChatSession, 'title' | 'subtitle' | 'avatarText'>;

/**
 * Resolve identity-dependent session copy from the canonical read models.
 *
 * Persisted/transport sessions may contain placeholders, but those snapshots
 * are never presentation authority. Re-running this selector after identity
 * hydration keeps contacts, conversation rows, and the active header aligned.
 */
export function resolveSessionPresentation(
  sessionType: SessionKind,
  sessionId: number,
  contactsById: DirectoryMap,
  groupsById: GroupMap,
): SessionPresentation {
  if (sessionType === 'group') {
    const group = groupsById[sessionId];
    return {
      title: group?.name || `群组 #${sessionId}`,
      subtitle: group?.shieldStatus ? '已开启免打扰' : '群组讨论',
      avatarText: initials(group?.name, 'GR'),
    };
  }

  const contact = contactsById[sessionId];
  return {
    title: contact?.name || `用户 #${sessionId}`,
    subtitle: contact?.signInfo || userStatusLabel(contact?.status ?? 0),
    avatarText: initials(contact?.name || contact?.email, String(sessionId).slice(-2)),
  };
}

export function projectSessionPresentation(
  session: ChatSession,
  contactsById: DirectoryMap,
  groupsById: GroupMap,
): ChatSession {
  const presentation = resolveSessionPresentation(
    session.sessionType,
    session.sessionId,
    contactsById,
    groupsById,
  );
  if (session.title === presentation.title &&
      session.subtitle === presentation.subtitle &&
      session.avatarText === presentation.avatarText) {
    return session;
  }
  return { ...session, ...presentation };
}

export function projectSessionMap(
  sessionsByKey: SessionMap,
  contactsById: DirectoryMap,
  groupsById: GroupMap,
): SessionMap {
  return Object.fromEntries(Object.entries(sessionsByKey).map(([sessionKey, session]) => [
    sessionKey,
    projectSessionPresentation(session, contactsById, groupsById),
  ]));
}

/**
 * Resolve a conversation's explicit avatar from the canonical read models.
 * ChatSession deliberately stores only relationship/session data: copying an
 * avatar URL into it would create another profile cache that can become stale.
 * UserAvatar remains responsible for the stable generated fallback when this
 * resolver returns an empty string.
 */
export function resolveSessionAvatarUrl(
  session: Pick<ChatSession, 'sessionId' | 'sessionType'>,
  contactsById: DirectoryMap,
  groupsById: GroupMap,
): string {
  if (session.sessionType === 'group') {
    return groupsById[session.sessionId]?.avatarUrl ?? '';
  }

  return contactsById[session.sessionId]?.avatarUrl ?? '';
}
